import React, { useState, useRef, memo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, Users, Heart, Globe, GraduationCap, School, Calendar, Eye, Layers, Compass, Sparkles, MessageCircle, ChevronRight, Building2, ShieldCheck, Mail, Send } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
// ---------- Shared visual primitives ----------
const Eyebrow = ({
  children
}: {
  children: React.ReactNode;
}) => <p className="text-amber-400/70 tracking-[0.3em] text-[10px] sm:text-xs uppercase font-light" data-id="element-6552">
    {children}
  </p>;
const SectionHeading = ({
  eyebrow,
  title,
  highlight,
  intro,
  align = 'center'
}: {
  eyebrow: string;
  title: string;
  highlight?: string;
  intro?: string;
  align?: 'center' | 'left';
}) => <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''}`} data-id="element-6553">
    <Eyebrow data-id="element-6554">{eyebrow}</Eyebrow>
    <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.05] tracking-tight" data-id="element-6555">
      {title}
      {highlight && <>
          {' '}
          <span className="text-amber-400" data-id="element-6556">{highlight}</span>
        </>}
    </h2>
    <div className={`w-16 h-px bg-amber-400/30 mb-6 ${align === 'center' ? 'mx-auto' : ''}`} data-id="element-6557" />
    {intro && <p className="text-base sm:text-lg text-white/65 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-6558">
        {intro}
      </p>}
  </div>;
const PremiumCard = ({
  icon: Icon,
  title,
  body,
  highlight = false
}: {
  icon: React.ElementType;
  title: string;
  body: string;
  highlight?: boolean;
}) => <div className={`group relative border ${highlight ? 'border-amber-400/40' : 'border-amber-400/15'} bg-gradient-to-b from-amber-950/10 to-transparent hover:border-amber-400/50 transition-colors duration-500 p-6 sm:p-7`} data-id="element-6559">
    <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-6560" />
    <div className="w-10 h-10 border border-amber-400/40 bg-black/40 flex items-center justify-center mb-5" data-id="element-6561">
      <Icon className="w-4 h-4 text-amber-400" data-id="element-6562" />
    </div>
    <h3 className="font-serif text-lg sm:text-xl text-white mb-3 leading-tight" data-id="element-6563">
      {title}
    </h3>
    <p className="text-sm sm:text-[15px] text-white/60 leading-relaxed font-light" data-id="element-6564">
      {body}
    </p>
  </div>;
// =====================================================================
// 1. HERO
// =====================================================================
const SchoolsHero = () => <section className="relative w-full min-h-[92vh] bg-black flex items-center justify-center overflow-hidden pt-20" data-id="element-6565">
    <div className="absolute inset-0" data-id="element-6566">
      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms" className="w-full h-full object-cover opacity-25" data-id="element-6567" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black" data-id="element-6568" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.08),_transparent_70%)] pointer-events-none" data-id="element-6569" />
    </div>

    <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-6570">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.1
    }} data-id="element-6571">
        <Link to="/" className="inline-flex items-center gap-2 text-amber-400/60 hover:text-amber-400 text-xs tracking-[0.25em] uppercase font-light mb-12 transition-colors" data-id="element-6572">
          <ArrowLeft className="w-3.5 h-3.5" data-id="element-6573" />
          Back to Life in the Sikh Kingdoms
        </Link>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 1,
      delay: 0.25
    }} data-id="element-6574">
        <p className="text-amber-400 tracking-[0.35em] text-[10px] sm:text-xs uppercase mb-7 font-light" data-id="element-6575">
          A School &amp; Curriculum Partnership Opportunity
        </p>
        <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl lg:text-[5.5rem] text-white mb-7 tracking-tight leading-[0.95]" data-id="element-6576">
          For Schools &amp;
          <br data-id="element-6577" />
          <span className="text-amber-400" data-id="element-6578">Curriculum Partners</span>
        </h1>
        <div className="w-24 h-px bg-amber-400/40 mx-auto mb-7" data-id="element-6579" />
        <p className="text-lg sm:text-xl md:text-2xl text-white/75 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-6580">
          An immersive Sikh history learning experience that supports identity,
          belonging, cultural understanding, and student engagement.
        </p>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.55
    }} className="mt-10 max-w-3xl mx-auto" data-id="element-6581">
        <p className="text-sm sm:text-base text-white/55 font-light leading-relaxed" data-id="element-6582">
          Life of the Sikh Kingdoms brings Sikh history to life through
          immersive storytelling, visual world-building, and culturally
          meaningful learning. It offers schools a modern way to help middle
          school and high school students connect with history, heritage, and
          identity in a format that feels vivid, memorable, and relevant.
        </p>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7,
      delay: 0.75
    }} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" data-id="element-6583">
        <a href="#interest-form" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-amber-400 text-black text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-300 transition-colors shadow-[0_0_40px_rgba(212,175,55,0.25)]" data-id="element-6584">
          Request a Conversation
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-6585" />
        </a>
        <a href="#program" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-6586">
          Explore the School Program
        </a>
      </motion.div>
    </div>

    <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2" animate={{
    y: [0, 10, 0]
  }} transition={{
    duration: 2,
    repeat: Infinity
  }} data-id="element-6587">
      <div className="w-px h-12 bg-gradient-to-b from-transparent via-amber-400/60 to-transparent" data-id="element-6588" />
    </motion.div>
  </section>;
// =====================================================================
// 2. WHY THIS MATTERS
// =====================================================================
const WhyThisMattersSection = () => {
  const cards = [{
    icon: Heart,
    title: 'Identity & Belonging',
    body: 'A space where Punjabi Sikh students see their history, heritage, and people centred in a way that feels alive and contemporary.'
  }, {
    icon: BookOpen,
    title: 'Sikh History Made Meaningful',
    body: 'A visual, story-led entry point into Sikh history that moves beyond textbooks and feels relevant to today\u2019s students.'
  }, {
    icon: Globe,
    title: 'Cultural Understanding for All Students',
    body: 'For non-Sikh students, a thoughtful introduction to Sikh history, culture, and the communities they share classrooms with.'
  }];
  return <section id="program" className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-6589">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/5 to-transparent pointer-events-none" data-id="element-6590" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-6591">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-6592">
          <SectionHeading eyebrow="The Case for Schools" title="Why This" highlight="Matters" intro="Many students encounter history as something distant, abstract, or disconnected from their lives. Life of the Sikh Kingdoms offers a more meaningful entry point — deepening heritage and representation for Punjabi Sikh students, and broadening cultural understanding for everyone in the classroom." data-id="element-6593" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-6xl mx-auto" data-id="element-6594">
          {cards.map((c, i) => <motion.div key={c.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} data-id="element-6595">
              <PremiumCard {...c} data-id="element-6596" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 3. WHO IT SERVES
// =====================================================================
const WhoItServesSection = () => <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6597">
    <div className="container mx-auto px-4 relative z-10" data-id="element-6598">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="text-center max-w-4xl mx-auto mb-14" data-id="element-6599">
        <Eyebrow data-id="element-6600">Who It Serves</Eyebrow>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.1] tracking-tight" data-id="element-6601">
          Especially Meaningful for Punjabi Sikh Students —{' '}
          <span className="text-amber-400" data-id="element-6602">Valuable for All Students</span>
        </h2>
        <div className="w-16 h-px bg-amber-400/30 mx-auto" data-id="element-6603" />
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto" data-id="element-6604">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="relative border border-amber-400/30 bg-gradient-to-br from-amber-950/15 to-transparent p-8 sm:p-10" data-id="element-6605">
          <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-6606" />
          <div className="flex items-center gap-3 mb-5" data-id="element-6607">
            <div className="w-10 h-10 border border-amber-400/50 bg-amber-400/10 flex items-center justify-center" data-id="element-6608">
              <Heart className="w-4 h-4 text-amber-400" data-id="element-6609" />
            </div>
            <Eyebrow data-id="element-6610">Heritage Centred</Eyebrow>
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl text-amber-400 mb-4 leading-tight" data-id="element-6611">
            For Punjabi Sikh Students
          </h3>
          <p className="text-white/70 text-[15px] sm:text-base font-light leading-relaxed" data-id="element-6612">
            Supports stronger connection to heritage, identity, pride, and
            cultural memory through a format that feels immersive and alive —
            history they can see themselves inside.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: 20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="relative border border-amber-400/15 bg-gradient-to-br from-white/[0.02] to-transparent p-8 sm:p-10" data-id="element-6613">
          <div className="flex items-center gap-3 mb-5" data-id="element-6614">
            <div className="w-10 h-10 border border-white/20 bg-white/5 flex items-center justify-center" data-id="element-6615">
              <Globe className="w-4 h-4 text-white/80" data-id="element-6616" />
            </div>
            <Eyebrow data-id="element-6617">Broader Learning</Eyebrow>
          </div>
          <h3 className="font-serif text-2xl sm:text-3xl text-white mb-4 leading-tight" data-id="element-6618">
            For All Students
          </h3>
          <p className="text-white/65 text-[15px] sm:text-base font-light leading-relaxed" data-id="element-6619">
            Builds cultural understanding, empathy, and Sikh history literacy —
            helping students better understand the communities, histories, and
            stories around them.
          </p>
        </motion.div>
      </div>
    </div>
  </section>;
// =====================================================================
// 4. MIDDLE / HIGH SCHOOL
// =====================================================================
const GradeLevelSection = () => <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6620">
    <div className="container mx-auto px-4 relative z-10" data-id="element-6621">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="mb-14" data-id="element-6622">
        <SectionHeading eyebrow="Grade Levels" title="Designed for" highlight="Middle School and High School Learners" intro="The experience is built for both middle school and high school student groups — strong visual storytelling, emotional accessibility, and meaningful historical entry points across age ranges." data-id="element-6623" />
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-id="element-6624">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="relative border border-amber-400/20 p-7 sm:p-8 hover:border-amber-400/45 transition-colors" data-id="element-6625">
          <div className="flex items-baseline justify-between mb-5" data-id="element-6626">
            <School className="w-5 h-5 text-amber-400" data-id="element-6627" />
            <span className="font-mono text-amber-400/50 text-[10px] tracking-[0.25em]" data-id="element-6628">
              GRADES 6–8
            </span>
          </div>
          <h3 className="font-serif text-2xl text-white mb-3 leading-tight" data-id="element-6629">
            Middle School
          </h3>
          <p className="text-white/60 text-sm font-light leading-relaxed" data-id="element-6630">
            A vivid and memorable introduction to Sikh history, culture, and
            identity — built to spark curiosity and connection at an
            age-appropriate level.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="relative border border-amber-400/20 p-7 sm:p-8 hover:border-amber-400/45 transition-colors" data-id="element-6631">
          <div className="flex items-baseline justify-between mb-5" data-id="element-6632">
            <GraduationCap className="w-5 h-5 text-amber-400" data-id="element-6633" />
            <span className="font-mono text-amber-400/50 text-[10px] tracking-[0.25em]" data-id="element-6634">
              GRADES 9–12
            </span>
          </div>
          <h3 className="font-serif text-2xl text-white mb-3 leading-tight" data-id="element-6635">
            High School
          </h3>
          <p className="text-white/60 text-sm font-light leading-relaxed" data-id="element-6636">
            A deeper opportunity for reflection, discussion, leadership themes,
            and historical understanding — supporting humanities, history, and
            equity learning.
          </p>
        </motion.div>
      </div>
    </div>
  </section>;
// =====================================================================
// 5. WHAT STUDENTS GAIN
// =====================================================================
const WhatStudentsGainSection = () => {
  const items = [{
    icon: Compass,
    title: 'Stronger Identity & Belonging',
    body: 'A space where students see themselves and their communities reflected in the story.'
  }, {
    icon: Eye,
    title: 'Sikh History Experienced Vividly',
    body: 'Story-led, visual learning that makes history feel alive rather than abstract.'
  }, {
    icon: Heart,
    title: 'Cultural Understanding & Empathy',
    body: 'A clearer, more human view of Sikh history and the communities around them.'
  }, {
    icon: Sparkles,
    title: 'Engagement Beyond the Textbook',
    body: 'A learning format that feels modern, immersive, and memorable for students.'
  }, {
    icon: MessageCircle,
    title: 'Reflection &amp; Discussion',
    body: 'Meaningful prompts for in-class conversation, follow-up, and connection.'
  }];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6637">
      <div className="container mx-auto px-4 relative z-10" data-id="element-6638">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-6639">
          <SectionHeading eyebrow="Student Outcomes" title="What Students" highlight="Gain" data-id="element-6640" />
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto" data-id="element-6641">
          {items.map((it, i) => <motion.div key={it.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className={i === 4 ? 'lg:col-start-2' : ''} data-id="element-6642">
              <PremiumCard {...it} data-id="element-6643" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 6. HOW SCHOOLS CAN PARTICIPATE
// =====================================================================
const HowSchoolsParticipateSection = () => {
  const ways = [{
    icon: Users,
    title: 'School Group Visits',
    body: 'Organized middle school and high school visits to the immersive experience, structured around group size and learning goals.'
  }, {
    icon: Eye,
    title: 'Educator Preview Sessions',
    body: 'Preview opportunities for school leaders, curriculum teams, and educators to experience the program before bringing students.'
  }, {
    icon: BookOpen,
    title: 'Curriculum Enrichment',
    body: 'A meaningful experience that complements Sikh history, heritage learning, and broader multicultural education.'
  }, {
    icon: ShieldCheck,
    title: 'Pilot Partnerships',
    body: 'Early partnership opportunities for schools and education leaders interested in exploring access, fit, and scale.'
  }];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6644">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/5 to-transparent pointer-events-none" data-id="element-6645" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-6646">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-14" data-id="element-6647">
          <SectionHeading eyebrow="Participation" title="How Schools" highlight="Can Participate" data-id="element-6648" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto" data-id="element-6649">
          {ways.map((w, i) => <motion.div key={w.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.08
        }} className="relative border border-amber-400/15 hover:border-amber-400/45 transition-colors p-7 flex gap-5 bg-gradient-to-br from-amber-950/5 to-transparent" data-id="element-6650">
              <span className="font-mono text-amber-400/40 text-xs tracking-[0.2em] shrink-0 pt-1" data-id="element-6651">
                0{i + 1}
              </span>
              <div className="flex-1" data-id="element-6652">
                <div className="flex items-center gap-3 mb-3" data-id="element-6653">
                  <w.icon className="w-4 h-4 text-amber-400" data-id="element-6654" />
                  <h3 className="font-serif text-lg sm:text-xl text-white leading-tight" data-id="element-6655">
                    {w.title}
                  </h3>
                </div>
                <p className="text-white/60 text-[14px] sm:text-[15px] font-light leading-relaxed" data-id="element-6656">
                  {w.body}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 7. CURRICULUM CONNECTION
// =====================================================================
const CurriculumConnectionSection = () => {
  const themes = ['Sikh history and heritage', 'Leadership and empire', 'Culture, art, and memory', 'Identity and continuity', 'Belonging and representation', 'Historical empathy', 'Cross-cultural learning'];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6657">
      <div className="container mx-auto px-4 relative z-10" data-id="element-6658">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start" data-id="element-6659">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="lg:col-span-3" data-id="element-6660">
            <Eyebrow data-id="element-6661">Curriculum &amp; Learning</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.1] tracking-tight" data-id="element-6662">
              Built for Enrichment, Reflection, and{' '}
              <span className="text-amber-400" data-id="element-6663">Deeper Learning</span>
            </h2>
            <div className="w-16 h-px bg-amber-400/30 mb-6" data-id="element-6664" />
            <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed mb-5" data-id="element-6665">
              Life of the Sikh Kingdoms is designed to support heritage
              learning, Sikh history exploration, identity-based reflection, and
              broader multicultural understanding.
            </p>
            <p className="text-white/55 text-sm sm:text-base font-light leading-relaxed" data-id="element-6666">
              It is intended to complement school and community learning by
              making history feel vivid, human, and memorable — designed to{' '}
              <span className="text-amber-400/90" data-id="element-6667">support</span>,{' '}
              <span className="text-amber-400/90" data-id="element-6668">complement</span>, and{' '}
              <span className="text-amber-400/90" data-id="element-6669">enrich</span> existing
              programming.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7,
          delay: 0.15
        }} className="lg:col-span-2 relative border border-amber-400/20 bg-amber-950/5 p-6 sm:p-7" data-id="element-6670">
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-6671" />
            <Eyebrow data-id="element-6672">Learning Themes</Eyebrow>
            <ul className="mt-5 space-y-3" data-id="element-6673">
              {themes.map((t, i) => <li key={t} className="flex items-start gap-3 text-white/70 text-sm font-light" data-id="element-6674">
                  <span className="font-mono text-amber-400/60 text-[10px] tracking-[0.25em] mt-1 shrink-0" data-id="element-6675">
                    0{i + 1}
                  </span>
                  <span data-id="element-6676">{t}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
};
// =====================================================================
// 8. DIGITAL EXTENSION (kept short & secondary)
// =====================================================================
const DigitalExtensionSection = () => <section className="py-20 md:py-24 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6677">
    <div className="container mx-auto px-4 relative z-10" data-id="element-6678">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="max-w-3xl mx-auto text-center" data-id="element-6679">
        <Eyebrow data-id="element-6680">Optional Extension</Eyebrow>
        <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-5 mb-5 leading-[1.15] tracking-tight" data-id="element-6681">
          Optional Digital Engagement{' '}
          <span className="text-amber-400" data-id="element-6682">Beyond the Visit</span>
        </h2>
        <div className="w-12 h-px bg-amber-400/30 mx-auto mb-6" data-id="element-6683" />
        <p className="text-white/60 text-[15px] sm:text-base font-light leading-relaxed" data-id="element-6684">
          An optional digital engagement layer can help students continue
          exploring Sikh history after the visit — through guided discovery,
          participation, and contemporary forms of engagement. Available as a
          supportive extension to the core in-person experience, not a
          replacement for it.
        </p>
        <div className="mt-7 inline-flex items-center gap-2 px-4 py-1.5 border border-amber-400/20 text-amber-400/70 text-[10px] tracking-[0.3em] uppercase" data-id="element-6685">
          <Layers className="w-3 h-3" data-id="element-6686" />
          Optional · Secondary to In-Person Experience
        </div>
      </motion.div>
    </div>
  </section>;
// =====================================================================
// 9. COMMUNITY / MENTORSHIP
// =====================================================================
const CommunityMentorshipSection = () => <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6687">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,_rgba(212,175,55,0.06),_transparent_60%)] pointer-events-none" data-id="element-6688" />
    <div className="container mx-auto px-4 relative z-10" data-id="element-6689">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.7
    }} className="max-w-4xl mx-auto text-center" data-id="element-6690">
        <Eyebrow data-id="element-6691">Beyond the Classroom</Eyebrow>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-[1.1] tracking-tight" data-id="element-6692">
          Learning That Can Extend{' '}
          <span className="text-amber-400" data-id="element-6693">Beyond the Experience</span>
        </h2>
        <div className="w-16 h-px bg-amber-400/30 mx-auto mb-7" data-id="element-6694" />
        <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed" data-id="element-6695">
          This work can also open the door to broader community connection,
          mentorship, and intergenerational learning. It creates opportunities
          for students to encounter history not only as content, but as
          something connected to people, memory, and cultural leadership in the
          present.
        </p>
      </motion.div>
    </div>
  </section>;
// =====================================================================
// 10. WHO WE WORK WITH
// =====================================================================
const WhoWeWorkWithSection = () => {
  const audiences = [{
    icon: Compass,
    label: 'Curriculum Leaders'
  }, {
    icon: Building2,
    label: 'Principals &amp; Vice Principals'
  }, {
    icon: BookOpen,
    label: 'Educators'
  }, {
    icon: ShieldCheck,
    label: 'Trustees'
  }, {
    icon: Heart,
    label: 'Equity &amp; Inclusion Teams'
  }, {
    icon: Users,
    label: 'Student Engagement Teams'
  }, {
    icon: Globe,
    label: 'Community Education Partners'
  }, {
    icon: GraduationCap,
    label: 'Sikh &amp; Punjabi Education Advocates'
  }];
  return <section className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6696">
      <div className="container mx-auto px-4 relative z-10" data-id="element-6697">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="mb-12" data-id="element-6698">
          <SectionHeading eyebrow="Our Partners" title="Who We" highlight="Hope to Work With" data-id="element-6699" />
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-w-5xl mx-auto" data-id="element-6700">
          {audiences.map((a, i) => <motion.div key={a.label} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.05
        }} className="relative border border-amber-400/15 hover:border-amber-400/40 bg-gradient-to-br from-amber-950/5 to-transparent p-5 transition-colors group" data-id="element-6701">
              <a.icon className="w-4 h-4 text-amber-400/70 group-hover:text-amber-400 mb-3 transition-colors" data-id="element-6702" />
              <p className="font-serif text-[15px] sm:text-base text-white leading-snug" dangerouslySetInnerHTML={{
            __html: a.label
          }} data-id="element-6703" />
            </motion.div>)}
        </div>
      </div>
    </section>;
};
// =====================================================================
// 11. CALL TO ACTION
// =====================================================================
const SchoolsCTASection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0, 1, 1, 0.6]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-24 md:py-32 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6704">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.12),_transparent_60%)] pointer-events-none" data-id="element-6705" />
      <div className="container mx-auto px-4 relative z-10" data-id="element-6706">
        <div className="max-w-3xl mx-auto text-center" data-id="element-6707">
          <Eyebrow data-id="element-6708">Take the Next Step</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mt-5 mb-7 leading-[1.05] tracking-tight" data-id="element-6709">
            Bring Sikh History{' '}
            <span className="text-amber-400" data-id="element-6710">to Life for Students</span>
          </h2>
          <div className="w-20 h-px bg-amber-400/40 mx-auto mb-7" data-id="element-6711" />
          <p className="text-white/65 text-base sm:text-lg font-light leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-6712">
            We are looking to work with schools, curriculum partners, and
            education leaders who want to create more meaningful, engaging, and
            culturally grounded learning experiences for middle school and high
            school students.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" data-id="element-6713">
            <a href="#interest-form" className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-amber-400 text-black text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-300 transition-colors shadow-[0_0_40px_rgba(212,175,55,0.3)]" data-id="element-6714">
              Request a School Conversation
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-6715" />
            </a>
            <a href="#interest-form" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-amber-400/40 hover:border-amber-400 text-amber-400 text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-amber-400/5 transition-colors" data-id="element-6716">
              Invite Us to Present
            </a>
          </div>
        </div>
      </div>
    </motion.section>;
};
// =====================================================================
// 12. INTEREST FORM
// =====================================================================
const InterestFormSection = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    school: '',
    role: '',
    grade: 'both',
    email: '',
    phone: '',
    interest: '',
    conversation: 'preview',
    comments: ''
  });
  const update = (k: string, v: string) => setForm(prev => ({
    ...prev,
    [k]: v
  }));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const Field = ({
    label,
    children,
    optional = false
  }: {
    label: string;
    children: React.ReactNode;
    optional?: boolean;
  }) => <label className="block" data-id="element-6717">
      <span className="text-amber-400/70 text-[10px] tracking-[0.3em] uppercase mb-2 block" data-id="element-6718">
        {label}
        {optional && <span className="text-white/30 normal-case tracking-normal ml-2" data-id="element-6719">
            (optional)
          </span>}
      </span>
      {children}
    </label>;
  const inputCls = 'w-full bg-black border border-amber-400/20 px-4 py-3 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-amber-400/60 transition-colors';
  return <section id="interest-form" className="py-20 md:py-28 bg-black relative overflow-hidden border-t border-amber-400/10" data-id="element-6720">
      <div className="container mx-auto px-4 relative z-10" data-id="element-6721">
        <div className="max-w-3xl mx-auto" data-id="element-6722">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center mb-12" data-id="element-6723">
            <Eyebrow data-id="element-6724">Get in Touch</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-5 mb-6 leading-tight" data-id="element-6725">
              Share Your <span className="text-amber-400" data-id="element-6726">Interest</span>
            </h2>
            <div className="w-16 h-px bg-amber-400/30 mx-auto mb-6" data-id="element-6727" />
            <p className="text-white/55 text-sm sm:text-base font-light max-w-xl mx-auto leading-relaxed" data-id="element-6728">
              Tell us about your school or organization. We'll follow up with
              the right next step — whether that's a preview, a presentation, or
              a partnership conversation.
            </p>
          </motion.div>

          {submitted ? <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="relative border border-amber-400/40 bg-amber-950/10 p-10 sm:p-12 text-center" data-id="element-6729">
              <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" data-id="element-6730" />
              <div className="w-12 h-12 mx-auto border border-amber-400/50 bg-amber-400/10 flex items-center justify-center mb-5" data-id="element-6731">
                <Send className="w-5 h-5 text-amber-400" data-id="element-6732" />
              </div>
              <h3 className="font-serif text-2xl text-white mb-3" data-id="element-6733">
                Thank you — we'll be in touch
              </h3>
              <p className="text-white/55 text-sm font-light leading-relaxed max-w-md mx-auto" data-id="element-6734">
                Your interest has been received. A member of the school
                partnerships team will reach out shortly to set up the right
                next conversation.
              </p>
            </motion.div> : <motion.form onSubmit={handleSubmit} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative border border-amber-400/20 bg-gradient-to-b from-amber-950/8 to-transparent p-7 sm:p-10 space-y-6" data-id="element-6735">
              <span className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" data-id="element-6736" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-6737">
                <Field label="Name" data-id="element-6738">
                  <input type="text" required value={form.name} onChange={e => update('name', e.target.value)} placeholder="Your full name" className={inputCls} data-id="element-6739" />
                </Field>
                <Field label="Role" data-id="element-6740">
                  <input type="text" required value={form.role} onChange={e => update('role', e.target.value)} placeholder="e.g. Principal, Curriculum Lead" className={inputCls} data-id="element-6741" />
                </Field>
              </div>

              <Field label="School / Organization" data-id="element-6742">
                <input type="text" required value={form.school} onChange={e => update('school', e.target.value)} placeholder="School or district name" className={inputCls} data-id="element-6743" />
              </Field>

              <Field label="Grade Level of Interest" data-id="element-6744">
                <div className="flex flex-wrap gap-2" data-id="element-6745">
                  {[{
                v: 'middle',
                l: 'Middle School'
              }, {
                v: 'high',
                l: 'High School'
              }, {
                v: 'both',
                l: 'Both'
              }].map(opt => {
                const active = form.grade === opt.v;
                return <button key={opt.v} type="button" onClick={() => update('grade', opt.v)} className={`px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase border transition-colors ${active ? 'bg-amber-400 text-black border-amber-400 font-semibold' : 'border-amber-400/25 text-white/60 hover:border-amber-400/60 hover:text-white'}`} data-id="element-6746">
                        {opt.l}
                      </button>;
              })}
                </div>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-6747">
                <Field label="Email" data-id="element-6748">
                  <input type="email" required value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@school.org" className={inputCls} data-id="element-6749" />
                </Field>
                <Field label="Phone" optional data-id="element-6750">
                  <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+1 (___) ___-____" className={inputCls} data-id="element-6751" />
                </Field>
              </div>

              <Field label="Area of Interest" data-id="element-6752">
                <input type="text" value={form.interest} onChange={e => update('interest', e.target.value)} placeholder="e.g. heritage learning, equity programming, school visit" className={inputCls} data-id="element-6753" />
              </Field>

              <Field label="What would you like first?" data-id="element-6754">
                <div className="flex flex-wrap gap-2" data-id="element-6755">
                  {[{
                v: 'preview',
                l: 'Educator Preview'
              }, {
                v: 'presentation',
                l: 'Presentation'
              }, {
                v: 'partnership',
                l: 'Partnership Conversation'
              }].map(opt => {
                const active = form.conversation === opt.v;
                return <button key={opt.v} type="button" onClick={() => update('conversation', opt.v)} className={`px-4 py-2.5 text-[10px] tracking-[0.25em] uppercase border transition-colors ${active ? 'bg-amber-400 text-black border-amber-400 font-semibold' : 'border-amber-400/25 text-white/60 hover:border-amber-400/60 hover:text-white'}`} data-id="element-6756">
                        {opt.l}
                      </button>;
              })}
                </div>
              </Field>

              <Field label="Comments" optional data-id="element-6757">
                <textarea rows={4} value={form.comments} onChange={e => update('comments', e.target.value)} placeholder="Anything you'd like us to know — context, timing, group size, etc." className={inputCls + ' resize-none'} data-id="element-6758" />
              </Field>

              <button type="submit" className="group w-full inline-flex items-center justify-center gap-2.5 px-7 py-4 bg-amber-400 text-black text-[11px] tracking-[0.3em] uppercase font-bold hover:bg-amber-300 transition-colors shadow-[0_0_40px_rgba(212,175,55,0.25)]" data-id="element-6759">
                <Mail className="w-4 h-4" data-id="element-6760" />
                Send to School Partnerships
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-6761" />
              </button>

              <p className="text-white/35 text-[10px] tracking-[0.2em] uppercase text-center" data-id="element-6762">
                We'll respond within 3 business days
              </p>
            </motion.form>}
        </div>
      </div>
    </section>;
};
// =====================================================================
// PAGE
// =====================================================================
export function SchoolsPage() {
  return <LanguageProvider data-id="element-6763">
      <div className="relative bg-black text-white overflow-x-hidden" data-id="element-6764">
        <ScrollProgress data-id="element-6765" />
        <Navbar data-id="element-6766" />
        <main className="w-full" data-id="element-6767">
          <SchoolsHero data-id="element-6768" />
          <WhyThisMattersSection data-id="element-6769" />
          <WhoItServesSection data-id="element-6770" />
          <GradeLevelSection data-id="element-6771" />
          <WhatStudentsGainSection data-id="element-6772" />
          <HowSchoolsParticipateSection data-id="element-6773" />
          <CurriculumConnectionSection data-id="element-6774" />
          <DigitalExtensionSection data-id="element-6775" />
          <CommunityMentorshipSection data-id="element-6776" />
          <WhoWeWorkWithSection data-id="element-6777" />
          <SchoolsCTASection data-id="element-6778" />
          <InterestFormSection data-id="element-6779" />
        </main>
        <Footer data-id="element-6780" />
      </div>
    </LanguageProvider>;
}